<template>
  <div>
    <!-- Banner Section -->
    <section :class="['confirmation-banner-mobile', bannerType]">
      <div v-if="!isError">
        <div
          v-if="showCfpsId"
          class="confirmation-banner-mobile__code-wrapper"
        >
          <img
            src="https://cdn.ampersend.io/3IK5A9o1V88yXHn2DTJUsDTwkmI.svg"
            alt="Confirmed checkmark"
            class="confirmation-banner-mobile__check"
          >
          <div class="confirmation-banner-mobile__code-text">
            <span class="confirmation-banner-mobile__code-label">Your Confirmation Code:</span>
            <strong
              ref="cfpsId"
              class="confirmation-banner-mobile__code-value"
            >{{ cfpsId | formatCfpsId }}</strong>
          </div>
        </div>

        <p class="confirmation-banner-mobile__description">
          Your USPS Change-of-Address has been submitted and is complete. No further action
          is required. You'll receive a <b>confirmation email to {{ email || 'the email provided' }}</b> shortly with more details.
        </p>

        <div class="confirmation-banner-mobile__accordion">
          <button
            class="confirmation-banner-mobile__accordion-header"
            type="button"
            @click="toggleSection('save')"
          >
            <span>1. Save This Confirmation</span>
            <svg
              class="confirmation-banner-mobile__chevron"
              :class="{ 'confirmation-banner-mobile__chevron--open': openSections.save }"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4 6l4 4 4-4"
                stroke="#000000"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
          <div
            v-if="openSections.save"
            class="confirmation-banner-mobile__accordion-content"
          >
            <p>
              Please save this confirmation page for your records. Local agencies and/or
              resources may require it for proof of your address.
            </p>
            <a
              href="#"
              class="confirmation-banner-mobile__link"
              @click.prevent="saveConfirmation"
            >Save Confirmation</a>
          </div>
        </div>

        <div class="confirmation-banner-mobile__accordion">
          <button
            class="confirmation-banner-mobile__accordion-header"
            type="button"
            @click="toggleSection('mail')"
          >
            <span>2. Keep an Eye on Your Mail</span>
            <svg
              class="confirmation-banner-mobile__chevron"
              :class="{ 'confirmation-banner-mobile__chevron--open': openSections.mail }"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4 6l4 4 4-4"
                stroke="#000000"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
          <div
            v-if="openSections.mail"
            class="confirmation-banner-mobile__accordion-content"
          >
            <p>
              You should receive a mailed communication from the U.S. Postal Service
              at your <b>new address</b> within 7-10 Postal business days of your mail
              forwarding date.
            </p>
          </div>
        </div>

        <a
          href="/mgo/disclaimer"
          class="confirmation-banner-mobile__change-address"
        >Change Another Address</a>
      </div>

      <div
        v-else
        id="in-person-proofing"
      >
        <div class="confirmation-banner-mobile__error-header">
          <img
            src="https://cdn.ampersend.io/3IK5AYntx6YqUkVTsyAuB54SaMK.svg"
            alt="error Icon"
            class="confirmation-banner__error-icon"
          >
          <h3 class="confirmation-banner-mobile__error-title">
            Your Change-of-Address is NOT Complete
          </h3>
        </div>
        <p class="confirmation-banner-mobile__error-description">
          {{ errorMessage }}
        </p>

        <div class="confirmation-banner-mobile__next-steps">
          <h4 class="confirmation-banner-mobile__next-steps-title">
            What Happens Next
          </h4>
          <p class="confirmation-banner-mobile__next-steps-text">
            You will receive an email to <strong>{{ email || 'the email provided' }}</strong> or mailed letter to <strong>{{ oldAddress.streetAddress || 'your old address' }}</strong> with instructions to complete your request. Action must be taken by <strong>{{ actionDeadline }}</strong>.
          </p>
        </div>

        <a
          href="/mgo/disclaimer"
          class="confirmation-banner-mobile__change-address"
        >Change Another Address</a>
      </div>
    </section>

    <!-- Success Page Section -->
    <section class="confirmation-success">
      <div class="confirmation-success__wrapper">
        <div class="confirmation-success__left">
          <div class="powered-by-header">
            <img
              src="https://cdn.ampersend.io/3IK5BRsDHEPWPkSRVLnyIPGHz69.svg"
              alt="Powered by MYMOVE"
              class="mymove-logo-header"
            >
          </div>

          <h1 class="confirmation-success__heading">
            <template v-if="moverType === 'BUSINESS'">
              Next, Complete To-Dos for Your
              New Address with MYMOVE
            </template>
            <template v-else>
              Next, Autofill Forms and Tackle
              Your Checklist in Minutes
            </template>
          </h1>

          <p class="confirmation-success__subheading">
            <template v-if="moverType === 'BUSINESS'">
              MYMOVE is proud to be an authorized affiliate of USPS, helping you get back to business faster with seamless service setup and local discounts.
            </template>
            <template v-else>
              Set up home services such as internet, home security and more with MYMOVE, an authorized affiliate of USPS.
            </template>
          </p>

          <div class="autofill-panel">
            <div class="autofill-panel__info">
              <h3 class="autofill-panel__heading">
                Start by Autofilling Your Info
              </h3>

              <div
                v-if="hasRealData"
                class="info-section-list"
              >
                <div class="info-section">
                  <div class="info-section__content">
                    <template v-if="moverType === 'BUSINESS'">
                      <div>{{ businessName }}</div>
                    </template>
                    <template v-else>
                      <div>{{ firstName }} {{ lastName }}</div>
                    </template>
                    <div>{{ formatPhoneNumber(mobilePhone) }}</div>
                    <div>{{ email }}</div>
                  </div>
                </div>
                <img
                  src="https://cdn.ampersend.io/3HltxVrfEPVANbGNFzDW38jB807.png"
                  alt="Line break"
                  class=""
                >
                <div class="info-section">
                  <div class="info-section__label">
                    Old Address
                  </div>
                  <div class="info-section__content">
                    <div>{{ formattedOldAddress }}</div>
                  </div>
                </div>

                <div class="info-section">
                  <div class="info-section__label">
                    New Address
                  </div>
                  <div class="info-section__content">
                    <div>{{ formattedNewAddress }}</div>
                  </div>
                </div>
                <div class="info-section">
                  <div class="info-section__label">
                    Move Date
                  </div>
                  <div class="info-section__content">
                    <div>{{ userMoveDate }}</div>
                  </div>
                </div>
              </div>
              <div
                v-else
                class="info-checklist"
              >
                <div
                  v-for="(item, index) in infoItems"
                  :key="index"
                  class="info-checklist__item"
                >
                  <svg
                    class="info-item__check"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13.5 4L6 11.5L2.5 8"
                      stroke="#004B87"
                      stroke-width="1.6"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <span>{{ item.value }}</span>
                </div>
              </div>
            </div>

            <div
              v-if="moverType === 'BUSINESS'"
              class="autofill-panel__consent"
            >
              <p class="autofill-panel__consent-text">
                By selecting “Yes, Autofill My Info”, I am voluntarily providing
                my consent to disclose a copy of my personal contact and change of 
                address information above to MYMOVE in order to autofill my MYMOVE 
                registration. I understand that I am leaving USPS.com and being redirected 
                to the MYMOVE website. I understand that I will be bound by MYMOVE’s Privacy 
                Policy and will no longer be subject to USPS’s Privacy Policy. I understand that,
                regardless of my consent to transfer my change of address and contact information, 
                I will be presented with advertisements for services such as internet, security, 
                insurance, and more. I understand that my data may be shared with those entities 
                for any purpose, including for non-move related purposes. My contact information 
                will not be retained by MYMOVE until I have had an opportunity to review and accept 
                MYMOVE's Privacy Policy and Terms and Conditions and have taken action to register with MYMOVE.
              </p>
            </div>
            <div
              v-else
              class="autofill-panel__consent"
            >
              <p class="autofill-panel__consent-text">
                By selecting “Yes, Autofill My Info”, I am voluntarily providing my consent to disclose a
                copy of my personal contact and change of address information above to MYMOVE in order to
                autofill my MYMOVE registration. I understand that I am leaving USPS.com and being redirected 
                to the MYMOVE website. I understand that I will be bound by MYMOVE’s Privacy Policy and will no 
                longer be subject to USPS’s Privacy Policy. I understand that, regardless of my consent to transfer 
                my change of address and contact information, I will be presented with advertisements for services 
                such as internet, security, insurance, and more. I understand that my data may be shared with those 
                entities for any purpose, including for non-move related purposes. My contact information will not 
                be retained by MYMOVE until I have had an opportunity to review and accept MYMOVE's Privacy Policy 
                and Terms and Conditions and have taken action to register with MYMOVE.
              </p>
            </div>

            <div class="autofill-panel__checkbox">
              <label class="autofill-checkbox-label">
                <input
                  id="autofill-consent-checkbox"
                  v-model="autofillSelected"
                  type="checkbox"
                  @change="trackAutofill"
                >
                <span class="checkbox-text">Yes, Autofill My Info</span>
              </label>
            </div>

            <button
              class="autofill-panel__button"
              @click="next"
            >
              Next
            </button>
          </div>

          <div class="offers-list">
            <div
              v-if="moverType !== 'BUSINESS' && successPageData.firstBox"
              class="offer-item"
            >
              <div class="offer-item__icon">
                <img
                  :src="tileSelection(successPageData.firstBox).icon"
                  :alt="tileSelection(successPageData.firstBox).heading"
                >
              </div>
              <div class="offer-item__content">
                <div class="offer-item__heading">
                  {{ tileSelection(successPageData.firstBox).heading }}
                </div>
                <div class="offer-item__subheading">
                  {{ tileSelection(successPageData.firstBox).subheading }}
                </div>
              </div>
            </div>

            <div
              v-if="successPageData.secondBox"
              class="offer-item"
            >
              <div class="offer-item__icon">
                <img
                  :src="tileSelection(successPageData.secondBox).icon"
                  :alt="tileSelection(successPageData.secondBox).heading"
                >
              </div>
              <div class="offer-item__content">
                <div class="offer-item__heading">
                  {{ tileSelection(successPageData.secondBox).heading }}
                </div>
                <div class="offer-item__subheading">
                  {{ tileSelection(successPageData.secondBox).subheading }}
                </div>
              </div>
            </div>

            <div
              v-if="successPageData.thirdBox"
              class="offer-item"
            >
              <div class="offer-item__icon">
                <img
                  :src="tileSelection(successPageData.thirdBox).icon"
                  :alt="tileSelection(successPageData.thirdBox).heading"
                >
              </div>
              <div class="offer-item__content">
                <div class="offer-item__heading">
                  {{ tileSelection(successPageData.thirdBox).heading }}
                </div>
                <div class="offer-item__subheading">
                  {{ tileSelection(successPageData.thirdBox).subheading }}
                </div>
              </div>
            </div>

            <div
              v-if="successPageData.fourthBox"
              class="offer-item"
            >
              <div class="offer-item__icon">
                <img
                  :src="tileSelection(successPageData.fourthBox).icon"
                  :alt="tileSelection(successPageData.fourthBox).heading"
                >
              </div>
              <div class="offer-item__content">
                <div class="offer-item__heading">
                  {{ tileSelection(successPageData.fourthBox).heading }}
                </div>
                <div class="offer-item__subheading">
                  {{ tileSelection(successPageData.fourthBox).subheading }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { format, parse, isValid, addDays } from 'date-fns';
import { track } from '../../analytics/events';
import { validateHandoffUrl } from '../../utils/commonUtil';
import { getStateName } from '../../utils/stateNames';

export default {
  name: 'MobileConfirmationSuccess',
  filters: {
    formatCfpsId(value) {
      return (value || '')
        .split('')
        .reduce((acc, char, indx) => {
          if (indx > 0 && indx < 15 && indx % 4 === 3) {
            return `${acc}${char}-`;
          }
          return `${acc}${char}`;
        }, '');
    },
  },
  props: {
    moverType: {
      type: String,
      default: '',
    },
    forwardType: {
      type: String,
      default: '',
    },
    showCfpsId: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      autofillSelected: false,
      loading: false,
      offersSelected: [],
      successPageData: {
        firstBox: '1',
        secondBox: '2',
        thirdBox: '3',
        fourthBox: '4',
      },
      email: '',
      firstName: '',
      lastName: '',
      businessName: '',
      mobilePhone: '',
      oldAddress: {},
      newAddress: {},
      userMoveDate: '',
      openSections: {
        save: false,
        mail: false,
      },
    };
  },
  computed: {
    autofill: () => JSON.parse(sessionStorage.getItem('autofill')),
    doubleJeopardy() {
      return [this.autofill, this.autofillSelected].includes(true);
    },
    passedIvsVerification() {
      return JSON.parse(sessionStorage.getItem('passedIvsVerification'));
    },
    ippRegistrationSuccess() {
      return JSON.parse(sessionStorage.getItem('ippRegistrationSuccess'));
    },
    isError() {
      return this.ippRegistrationSuccess === false || this.passedIvsVerification === false;
    },
    bannerType() {
      return this.isError ? 'confirmation-banner-mobile--error' : 'confirmation-banner-mobile--success';
    },
    errorMessage() {
      if (this.ippRegistrationSuccess === false && this.passedIvsVerification === false) {
        return 'Please visit your local Post Office and complete a hard copy Change-of-Address form.';
      }
      if (this.ippRegistrationSuccess === true && this.passedIvsVerification === false) {
        return 'We could not verify your identity. Additional identity verification is required to activate your request.';
      }
      return '';
    },
    cfpsId() {
      const { search } = window.location;
      const params = new URLSearchParams(search);
      return params.get('cfpsId') || '';
    },
    actionDeadline() {
      return format(addDays(new Date(), 20), 'MM/dd/yyyy');
    },
    isFirstNameFallback() {
      return this.firstName === 'Full Name';
    },
    isBusinessNameFallback() {
      return !this.businessName;
    },
    isMobilePhoneFallback() {
      return this.mobilePhone === 'Phone Number';
    },
    isEmailFallback() {
      return this.email === 'Email Address';
    },
    isMoveDateFallback() {
      return this.userMoveDate === 'Move Date';
    },
    isOldAddressFallback() {
      return !this.oldAddress.streetAddress;
    },
    isNewAddressFallback() {
      return !this.newAddress.streetAddress;
    },
    formattedOldAddress() {
      return this.formatAddress(this.oldAddress) || 'Old Address';
    },
    formattedNewAddress() {
      return this.formatAddress(this.newAddress) || 'New Address';
    },
    infoItems() {
      const nameItem = this.moverType === 'BUSINESS'
        ? { value: this.businessName || 'Business Name', isFallback: this.isBusinessNameFallback }
        : { value: `${this.firstName} ${this.lastName}`.trim(), isFallback: this.isFirstNameFallback };

      return [
        nameItem,
        { value: this.mobilePhone, isFallback: this.isMobilePhoneFallback },
        { value: this.email, isFallback: this.isEmailFallback },
        { value: this.userMoveDate, isFallback: this.isMoveDateFallback },
        { value: this.formattedOldAddress, isFallback: this.isOldAddressFallback },
        { value: this.formattedNewAddress, isFallback: this.isNewAddressFallback },
      ];
    },
    hasRealData() {
      return !this.isFirstNameFallback
        || !this.isMobilePhoneFallback
        || !this.isEmailFallback
        || !this.isMoveDateFallback
        || !this.isOldAddressFallback
        || !this.isNewAddressFallback;
    },
  },
  mounted() {
    this.loadDataFromStore();
  },
  methods: {
    toggleSection(section) {
      this.openSections[section] = !this.openSections[section];
    },
    saveConfirmation() {
      window.print();
    },
    trackAutofill() {
      track('ElementClicked', {
        name: `autofill-toggled-${this.doubleJeopardy ? 'on' : 'off'}`,
      });
    },
    formatMoveDate(dateStr) {
      if (!dateStr) return '';
      const parsedDate = parse(dateStr, 'yyyy-MM-dd', new Date());
      return isValid(parsedDate) ? format(parsedDate, 'MMMM d, yyyy') : '';
    },
    formatAddress(address) {
      if (!address || !address.streetAddress) return '';
      const {
        streetAddress, city, state, zipCode,
      } = address;
      const cityStateZip = [city, state].filter(Boolean).join(', ');
      return [streetAddress, [cityStateZip, zipCode].filter(Boolean).join(' ')]
        .filter(Boolean)
        .join(', ');
    },
    formatPhoneNumber(phone) {
      if (!phone || phone === 'Phone Number') return phone;
      const digits = phone.replace(/\D/g, '');
      if (digits.length === 10) {
        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
      }
      return phone;
    },
    loadDataFromStore() {
      if (!this.$store || !this.$store.get) return;

      const bridge = window.mgoAmpersend;
      const context = bridge && typeof bridge.getSuccessPageContext === 'function'
        ? bridge.getSuccessPageContext()
        : {};

      this.email = this.$store.get('instantSavings/email') || 'Email Address';
      this.firstName = this.$store.get('instantSavings/firstName') || 'Full Name';
      this.lastName = this.$store.get('instantSavings/lastName') || '';
      this.businessName = this.$store.get('instantSavings/businessName') || '';
      this.mobilePhone = this.$store.get('instantSavings/mobilePhone') || 'Phone Number';
      this.oldAddress = this.$store.get('instantSavings/oldAddress') || {};
      this.newAddress = this.$store.get('instantSavings/newAddress') || {};
      this.userMoveDate = this.formatMoveDate(context.userMoveDate) || 'Move Date';
    },
    tileSelection(tileNumber) {
      const tiles = {
        1: {
          heading: 'Set Up Internet & TV',
          subheading: 'Transfer your current service or set up a new one.',
         icon: 'https://cdn.ampersend.io/3IK5CM7dwWdLjOGEgxO2SPFOouH.svg',
        },
        2: {
          heading: 'Claim Mover Offers',
          subheading: 'Find savings for local and top national brands.',
          icon: 'https://cdn.ampersend.io/3IK5BpjXd7D0jVqygDlHNHVpu36.svg',
        },
        3: {
          heading: 'Update Auto Insurance',
          subheading: `${this.newAddress.state ? getStateName(this.newAddress.state) : 'Your state'} may require an update within 30 days.`,
          icon: 'https://cdn.ampersend.io/3IK5BQ9gHUNnUoSFoaoZBbI1txF.svg',
        },
        4: {
          heading: this.moverType === 'BUSINESS' ? 'Protect Your New Address' : 'Protect Your Home',
          subheading: this.moverType === 'BUSINESS' ? 'Get a security system quote.' : 'Get a home security quote for renters and homeowners.',
          icon: 'https://cdn.ampersend.io/3IK5BR03xVxTTBJd89EXW3Fdmkp.svg',
        },
      };
      return tiles[tileNumber] || tiles[2];
    },
    async next() {
      if (this.loading) return;

      this.loading = true;

      track('ElementClicked', {
        name: 'confirmation-success-next-button',
        offersSelected: this.offersSelected,
        autofillSelected: this.autofillSelected,
      });

      sessionStorage.setItem('autofill', this.doubleJeopardy);
      sessionStorage.setItem('offersSelected', JSON.stringify(this.offersSelected));

      const { url } = await this.$coa.handoffGetURL('offers', this.doubleJeopardy, this.$route.query);

      const validUrl = validateHandoffUrl(url);
      if (!validUrl) {
        this.loading = false;
        return;
      }
      window.location.href = validUrl;
    },
  },
};
</script>

<style scoped>
/* Banner Styles */
.confirmation-banner-mobile {
  width: 100vw;
  box-sizing: border-box;
  margin: 0 calc(50% - 50vw) 16px;
  padding: 20px;
  background-color: #F8FBFA;
}

.confirmation-banner-mobile--error {
  background-color: #F4E3DB;
}

.confirmation-banner-mobile__code-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.confirmation-banner-mobile__check {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  margin-top: 2px;
}

.confirmation-banner-mobile__code-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.confirmation-banner-mobile__code-label {
  font-size: 22px;
  font-weight: 700;
  color: #000000;
  line-height: 28px;
}

.confirmation-banner-mobile__code-value {
  font-size: 22px;
  font-weight: 700;
  color: #000000;
  line-height: 28px;
}

.confirmation-banner-mobile__description {
  font-size: 16px;
  line-height: 22px;
  color: #000000;
  margin: 0 0 16px 0;
}

.confirmation-banner-mobile__accordion {
  border-top: 1px solid #D8D8D8;
}

.confirmation-banner-mobile__accordion:last-of-type {
  border-bottom: 1px solid #D8D8D8;
  margin-bottom: 10px;
}

.confirmation-banner-mobile__accordion-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 16px 0;
  background: none;
  border: none;
  font-size: 14px;
  font-weight: 700;
  color: #000000;
  cursor: pointer;
  text-align: left;
  outline: none;
}

.confirmation-banner-mobile__accordion-header::-moz-focus-inner {
  border: 0;
}

.confirmation-banner-mobile__chevron {
  transition: transform 0.2s ease;
  flex-shrink: 0;
}

.confirmation-banner-mobile__chevron--open {
  transform: rotate(180deg);
}

.confirmation-banner-mobile__accordion-content {
  padding: 0 16px 14px 16px;
  font-size: 14px;
  line-height: 20px;
  color: #000000;
}

.confirmation-banner-mobile__accordion-content p {
  margin: 0 0 8px 0;
}

.confirmation-banner-mobile__link {
  color: #0068BB;
  text-decoration: underline;
  font-size: 14px;
}

.confirmation-banner-mobile__change-address {
  display: inline-block;
  color: #0068BB;
  text-decoration: underline;
  font-size: 14px;
  margin-top: 4px;
}

.confirmation-banner-mobile__error-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.confirmation-banner-mobile__error-icon {
  flex-shrink: 0;
}

.confirmation-banner-mobile__error-title {
  font-size: 22px;
  font-weight: 700;
  color: #DA291C;
  margin: 0;
  line-height: 28px;
}

.confirmation-banner-mobile__error-description {
  font-size: 16px;
  line-height: 22px;
  color: #000000;
  margin: 0 0 16px 0;
}

.confirmation-banner-mobile__next-steps {
  background-color: #FFFFFF;
  border-radius: 4px;
  padding: 14px 16px;
  margin-bottom: 16px;
}

.confirmation-banner-mobile__next-steps-title {
  font-size: 14px;
  font-weight: 700;
  color: #000000;
  margin: 0 0 8px 0;
}

.confirmation-banner-mobile__next-steps-text {
  font-size: 14px;
  line-height: 20px;
  color: #000000;
  margin: 0;
}

/* Success Page Styles */
.confirmation-success {
  max-width: 100%;
  margin: 0;
  /* padding: 16px; */
}

.confirmation-success__wrapper {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.confirmation-success__left {
  padding-right: 0;
}

.powered-by-header {
  margin-bottom: 12px;
  padding-left: 0;
  padding-top: 0;
}

.mymove-logo-header {
  height: 24px;
  width: 134px;
}

.confirmation-success__heading {
  font-size: 22px;
  font-weight: 700;
  color: #000000;
  line-height: 28px;
  letter-spacing: 0;
  margin-bottom: 8px;
  padding-left: 0;
}

.confirmation-success__subheading {
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0;
  color: #000000;
  padding-left: 0;
  padding-bottom: 16px;
}

.offers-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
}

.offer-item {
  display: flex;
  align-items: center;
  padding: 12px;
  border-radius: 8px;
}

.offer-item__icon {
  width: 40px;
  height: 40px;
  margin-right: 8px;
  flex-shrink: 0;
}

.offer-item__icon img {
  width: 100%;
  height: 100%;
}

.offer-item__heading {
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0;
  color: #000000;
  margin-bottom: 4px;
}

.offer-item__subheading {
  font-weight: 400;
  font-size: 12px;
  line-height: 14px;
  letter-spacing: 0;
  color: #000000;
}

/* .confirmation-success__right {
  position: static;
} */

.autofill-panel {
  background: #E7F6F8;
  border-radius: 8px;
  padding: 16px;
}


.autofill-panel__info {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
  background: white;
  padding: 16px;
  border-radius: 4px;
}

.info-section-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.autofill-panel__heading{
    font-family: Inter;
    font-weight: 700;
    font-style: Bold;
    font-size: 16px;
    line-height: 22px;
    letter-spacing: 0;
    color: #000000;

}

.info-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-section__label {
  font-weight: 700;
  font-size: 12px;
  line-height: 100%;
  letter-spacing: 0%;
  color: #000000;
}

.info-section__content {
  font-weight: 400;
  font-size: 12px;
  line-height: 14px;
  letter-spacing: 0;
  vertical-align: middle;
  color: #000000;
}

.info-checklist {
  display: grid;
  grid-auto-flow: column;
  grid-template-rows: repeat(3, auto);
  column-gap: 24px;
  row-gap: 12px;
}

.info-checklist__item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 400;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0;
  color: #000000;
}

.info-item__check {
  flex-shrink: 0;
}

.autofill-panel__consent {
  margin-bottom: 20px;
}

.autofill-panel__consent-text {
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  letter-spacing: 0;
  color: #000000;
}

.autofill-panel__checkbox {
  margin-bottom: 24px;
}

.autofill-checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 16px;
  border: 1px solid #004B87;
  border-radius: 4px;
  background: white;
  transition: all 0.2s ease;
}

.autofill-checkbox-label:hover {
  background-color: #f8f8ff;
}

.autofill-checkbox-label input[type="checkbox"] {
  width: 20px;
  height: 20px;
  margin-right: 12px;
  cursor: pointer;
  accent-color: #004B87;
}

.checkbox-text {
  font-size: 16px;
  font-weight: 700;
  color: #004B87;
}

.autofill-panel__button {
  width: 100%;
  padding: 14px 24px;
  background-color: #004B87;
  color: white;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  height: 52px;
}

.autofill-panel__button:hover {
  background-color: #ededed;
  color: #004b87;
}
.autofill-panel__button:active {
  background-color: #fff;
  color: #004b87;
}
</style>